/**
 * 共通のレイアウト（上の帯・左の項目立て・中身）。
 *
 * 左の項目は「できることの束」で出し分ける（docs/01 5-4）。並びは日々お使いになる順で、
 * 押せる項目だけを出す（決定21・menu/items.ts）。
 *
 * 画面の幅が狭いとき（タブレットなど・1024px 未満）は、項目立てを上の帯の「メニュー」から
 * 開く形にする。項目立てが本文の上に全部並び、本文が2画面ぶん下から始まるのを避けるため（総点検 M8）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type { DashboardAiCounts, DashboardMemberCounts } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { onMenuCountsChanged } from '../menu/badge-refresh.js';
import { MENU_SECTIONS, type MenuBadge } from '../menu/items.js';
import { can, visibleSections } from '../menu/visible.js';
import { LicensesLink } from '../pages/LicensesPage.js';
import { Button } from './ui/Button.js';

export function Layout(): ReactNode {
  const { profile, signOut } = useSession();
  const api = useApi();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiCounts, setAiCounts] = useState<DashboardAiCounts | null>(null);
  const [joinPending, setJoinPending] = useState<number | null>(null);
  /** 同じ画面の中で件数が変わったときに、赤丸を読み直すためのきっかけ */
  const [countsKey, setCountsKey] = useState(0);
  const canApprove = profile ? can(profile.grant, 'ai_answer.approve') : false;
  const canManageMembers = profile ? can(profile.grant, 'member.manage') : false;

  // 別の画面へ移ったら、狭い幅で開いていた項目立てを閉じる
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => onMenuCountsChanged(() => setCountsKey((n) => n + 1)), []);

  // 承認待ち・答えられなかった質問の件数（赤丸）。画面を移るたびに読み直す。
  // 赤丸は目安の飾りなので、読めなかったときは赤丸を出さないだけにする（画面は止めない）
  useEffect(() => {
    if (!canApprove) return;
    let cancelled = false;
    api
      .get<DashboardAiCounts>('/admin/dashboard/ai')
      .then((loaded) => {
        if (!cancelled) setAiCounts(loaded);
      })
      .catch(() => {
        if (!cancelled) setAiCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, canApprove, location.pathname, countsKey]);

  // 参加の申し込み（承認待ち）の件数（赤丸・決定34）。上と同じく、読めなければ出さないだけ
  useEffect(() => {
    if (!canManageMembers) return;
    let cancelled = false;
    api
      .get<DashboardMemberCounts>('/admin/dashboard/members')
      .then((loaded) => {
        if (!cancelled) setJoinPending(loaded.join_requests_pending ?? null);
      })
      .catch(() => {
        if (!cancelled) setJoinPending(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, canManageMembers, location.pathname, countsKey]);

  if (!profile) return null;

  const sections = visibleSections(MENU_SECTIONS, profile.grant);
  const scopeLabel = profile.grant.scope === 'org' ? '全社' : '自拠点';

  function badgeCount(badge: MenuBadge | undefined): number {
    if (!badge) return 0;
    if (badge === 'join_requests') return joinPending ?? 0;
    if (!aiCounts) return 0;
    return badge === 'ai_pending' ? aiCounts.pending : aiCounts.unanswered;
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div className="admin-header-brand">
          <button
            type="button"
            className="admin-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="admin-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'メニューを閉じる' : 'メニュー'}
          </button>
          {/* 会社ごとの色を四角で添える（色の値は設定から・docs/06 1-1） */}
          <span className="admin-header-mark" aria-hidden="true" />
          <div>
            {/* 会社名はプログラムに書かず、会社ごとの表示設定から出す（docs/01 20-5） */}
            <strong>{profile.branding.app_name}</strong>{' '}
            <span className="admin-header-suffix">管理画面</span>
          </div>
        </div>
        <div className="admin-header-right">
          <span>
            {profile.me.display_name ?? 'ご利用者'}さん（{profile.roleDisplayName}・{scopeLabel}）
          </span>
          <Button variant="secondary" compact onClick={() => signOut()}>
            ログアウト
          </Button>
        </div>
      </header>

      <div className="admin-body">
        <nav
          id="admin-menu"
          className={`admin-menu${menuOpen ? ' is-open' : ''}`}
          aria-label="管理の操作"
        >
          {sections.map((section, index) => (
            <section key={section.title ?? `section-${index}`}>
              {section.title !== undefined ? <h2>{section.title}</h2> : null}
              <ul>
                {section.items.map((item) => {
                  const count = badgeCount(item.badge);
                  return (
                    <li key={item.path}>
                      <NavLink to={item.path} end={item.end === true}>
                        <span className="admin-menu-label">{item.label}</span>
                        {count > 0 ? (
                          <span className="admin-menu-count" aria-label={`${count}件`}>
                            {count > 99 ? '99+' : count}
                          </span>
                        ) : null}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {/* 使っている部品のライセンス（issue #486）。項目立ての下に小さく置く */}
          <LicensesLink />
        </nav>

        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
